import { useStore } from '@/infra/store';
import { eventBus } from '@/infra/event-bus/EventBus';
import { logger } from '@/infra/logger/Logger';
import { uuid } from '@/infra/util/uuid';
import type { BiomeId } from '@/domain/world/types';
import type { Tree, WorldEvent } from '@/domain/world/evolve';
import { ageTree, treeStageFor } from '@/domain/world/evolve';
import { createMemorial } from '@/domain/world/memorial';
import type { MemorialCause } from '@/domain/world/memorial';
import { isReady } from '@/domain/world/afk';
import { regionNpcSpots } from '@/domain/world/region';
import type { NpcCreature } from '@/domain/npc/types';
import type { WildEnemyRuntime } from '@/domain/combat/types';
import { recordWildDiscovery as aggregateWildDiscovery } from '@/domain/world/wild-bestiary';

const TICK_MS_PROD = 60_000;
const TICK_MS_FAST = 600;
const CATCHUP_MAX_MS = 14 * 24 * 60 * 60_000;

const FAST = import.meta.env.VITE_FAST_LIFECYCLE === '1';
const TICK_MS = FAST ? TICK_MS_FAST : TICK_MS_PROD;

let lastTickAt = 0;
let intervalId: ReturnType<typeof setInterval> | null = null;

function npcSpotForId(npcId: string, biome: BiomeId): { x: number; y: number } {
  const spots = regionNpcSpots(biome);
  let h = 0;
  for (let i = 0; i < npcId.length; i++) h = (h * 31 + npcId.charCodeAt(i)) | 0;
  return spots[Math.abs(h) % spots.length]!;
}

function tick(nowMs: number = Date.now()): void {
  const s = useStore.getState();
  const dt = nowMs - lastTickAt;
  lastTickAt = nowMs;
  if (dt <= 0 || !s.world) return;

  // Avança árvores
  for (const tree of s.world.trees ?? []) {
    const next = ageTree(tree, dt);
    if (next.stage !== tree.stage || next.ageMs !== tree.ageMs) {
      s.updateTree(tree.id, { ageMs: next.ageMs, stage: next.stage });
    }
  }

  // Marca AFK dispatches prontos como claimable
  for (const d of s.world.afkDispatches ?? []) {
    if (d.status === 'active' && isReady(d, nowMs)) {
      // Calcula rewards na demanda (lazy) — services/afk-expedition vai computar quando chamar markReady
      s.updateAfkDispatch(d.id, { status: 'claimable' });
    }
  }
}

function catchup(savedAtMs: number, nowMs: number): void {
  const dt = Math.min(CATCHUP_MAX_MS, Math.max(0, nowMs - savedAtMs));
  if (dt <= 0) {
    lastTickAt = nowMs;
    return;
  }
  const s = useStore.getState();
  // Avança árvores em batch
  const trees = s.world.trees ?? [];
  if (trees.length > 0) {
    const updated = trees.map((t) => ageTree(t, dt));
    s.setTrees(updated);
  }
  // Avança AFK dispatches: marca claimable se pronto
  for (const d of s.world.afkDispatches ?? []) {
    if (d.status === 'active' && isReady(d, nowMs)) {
      s.updateAfkDispatch(d.id, { status: 'claimable' });
    }
  }
  lastTickAt = nowMs;
  logger.info('world-evolution.catchup', { dtMs: dt, capped: dt === CATCHUP_MAX_MS });
}

function start(): void {
  if (intervalId !== null) return;
  lastTickAt = Date.now();
  intervalId = setInterval(() => tick(), TICK_MS);
  logger.info('world-evolution.started', {});
}

function stop(): void {
  if (intervalId !== null) {
    clearInterval(intervalId);
    intervalId = null;
  }
}

function onBreed(parentNpcId: string, parentNpcBiome: BiomeId | null): void {
  const s = useStore.getState();
  s.incrementGeneration();
  const generation = s.world.generation ?? 0;

  if (parentNpcBiome) {
    const pos = npcSpotForId(parentNpcId, parentNpcBiome);
    const tree: Tree = {
      id: `tree-${uuid()}`,
      biome: parentNpcBiome,
      x: pos.x + 40, // ligeiro offset para não pisar no NPC vivo
      y: pos.y + 20,
      plantedAtMs: Date.now(),
      plantedByNpcId: parentNpcId,
      stage: treeStageFor(0),
      ageMs: 0,
    };
    s.addTree(tree);
    const ev: WorldEvent = {
      id: `event-${uuid()}`,
      kind: 'tree-planted',
      biome: parentNpcBiome,
      occurredAtMs: Date.now(),
      generation,
      payload: { treeId: tree.id, npcId: parentNpcId },
    };
    s.addEvent(ev);
    eventBus.emit('world.tree-planted', { biome: parentNpcBiome, treeId: tree.id });
  }
}

function onNpcDeath(npc: NpcCreature, cause: MemorialCause): void {
  const s = useStore.getState();
  if (!npc.homeBiome) return;
  const pos = npcSpotForId(npc.id, npc.homeBiome);
  const memorial = createMemorial({
    npcId: npc.id,
    npcName: npc.name,
    cause,
    diedAtMs: npc.diedAt ?? Date.now(),
    biome: npc.homeBiome,
    x: pos.x,
    y: pos.y,
  });
  s.addMemorial(memorial);
  const ev: WorldEvent = {
    id: `event-${uuid()}`,
    kind: 'memorial',
    biome: npc.homeBiome,
    occurredAtMs: memorial.diedAtMs,
    generation: s.world.generation ?? 0,
    payload: { memorialId: memorial.id, npcId: npc.id, cause },
  };
  s.addEvent(ev);
  eventBus.emit('world.memorial', { npcId: npc.id, biome: npc.homeBiome });
}

function recordKill(enemyDefId: string): void {
  const s = useStore.getState();
  const before = s.world.enemyKills?.[enemyDefId] ?? 0;
  s.recordEnemyKill(enemyDefId);
  if (before === 0) {
    // Primeira vez: species-discovered event
    const ev: WorldEvent = {
      id: `event-${uuid()}`,
      kind: 'species-discovered',
      biome: 'meadow', // Default — não temos o bioma exato aqui
      occurredAtMs: Date.now(),
      generation: s.world.generation ?? 0,
      payload: { enemyId: enemyDefId },
    };
    s.addEvent(ev);
    eventBus.emit('world.species-discovered', { enemyId: enemyDefId });
  }
}

function recordWildDiscovery(enemy: WildEnemyRuntime): void {
  const s = useStore.getState();
  s.setWildBestiary(aggregateWildDiscovery(s.world.wildBestiary ?? {}, enemy));
}

export interface WorldEvolutionApi {
  start: () => void;
  stop: () => void;
  tick: (nowMs?: number) => void;
  catchup: (savedAtMs: number, nowMs: number) => void;
  onBreed: (parentNpcId: string, biome: BiomeId | null) => void;
  onNpcDeath: (npc: NpcCreature, cause: MemorialCause) => void;
  recordKill: (enemyDefId: string) => void;
  recordWildDiscovery: (enemy: WildEnemyRuntime) => void;
}

let api: WorldEvolutionApi | null = null;

export function getWorldEvolutionService(): WorldEvolutionApi {
  if (api) return api;
  api = { start, stop, tick, catchup, onBreed, onNpcDeath, recordKill, recordWildDiscovery };
  return api;
}

export function resetWorldEvolutionForTests(): void {
  stop();
  api = null;
  lastTickAt = 0;
}
